/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  Alert,
  Button,
  ButtonGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import swal from "sweetalert";
import { FiUpload } from "react-icons/fi";

// Hooks
import {
  useDocumentState,
  useDocumentHandlers,
  useSearchAndFilter,
  useDropzoneConfig,
  useDocumentUtils,
} from "./hooks";

// Sub-components
import {
  DocumentHeader,
  BreadcrumbNavigation,
  DocumentTable,
  DocumentUnitFoldersTable,
  UploadOptionsOverlay,
} from "./subcomponents";

// Existing components (no changes needed)
import AddNewFolder from "./AddNewFolder";
import EditFile from "./EditFile";
import EditFolder from "./EditFolder";
import UploadFile from "./UploadFile";
import UploadFolder from "./UploadFolder";
import FormPrompt from "../shared/FormPrompt";
import { ApiDocuments, ApiFolders } from "./ApiDocuments";

// Utilities
import {
  CheckPermission,
  customMultiValue,
  customOption,
  isUrdu,
} from "../../constants/const";
import { GetCustomStyles, GetSelectedLanguage } from "../helper/Method";
import { GetLanguageString } from "../helper/Components";
import { FaFolderPlus } from "react-icons/fa6";

/**
 * Refactored Documents Component
 * Clean, modular, and optimized version with separated concerns
 */
const Documents = ({ history }) => {
  const { t } = useTranslation();

  // State management
  const state = useDocumentState();

  // Handlers
  const handlers = useDocumentHandlers(state);

  // Search and filter
  const searchHandlers = useSearchAndFilter({ ...state, ...handlers });

  // Dropzone configuration
  const dropzoneHandlers = useDropzoneConfig(state);

  // Utility functions
  const { formatSize, getFileIcon, getUnitName } = useDocumentUtils();

  // ==================== Effects ====================

  /**
   * Handle click outside upload overlay
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        state.overlayRef.current &&
        !state.overlayRef.current.contains(event.target)
      ) {
        state.setShowUploadOptions(!state.showUploadOpions);
      }
    };

    if (state.showUploadOpions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [state.showUploadOpions]);

  /**
   * Handle Delete key press for file deletion
   */
  useEffect(() => {
    const handleDeleteKeyPress = (event) => {
      if (event.key === "Delete") {
        if (state.selectedRowIndex !== null) {
          const documentToDelete = state.documents[state.selectedRowIndex];
          state.setDetailBox(false);
          handlers.handleOnDelete(
            documentToDelete.id,
            documentToDelete.fileType,
            documentToDelete.folderHaveFilesOrFolders
          );
        }

        if (state.selectedDocumentsForDelete.length !== 0) {
          handlers.handleMultipleDelete();
        }
      }
    };

    window.addEventListener("keydown", handleDeleteKeyPress);

    return () => {
      window.removeEventListener("keydown", handleDeleteKeyPress);
    };
  }, [state.selectedRowIndex, state.selectedDocumentsForDelete]);

  /**
   * Load document tags on mount
   */
  useEffect(() => {
    ApiDocuments()
      .GetDocumentTags()
      .then((res) => {
        state.setDocumentTagsList(res.data);
      })
      .catch((err) => {
        console.log(err);
        swal({
          title: err,
          icon: "error",
          buttons: "OK",
        });
      });
  }, []);

  /**
   * Handle folder hierarchy changes
   */
  useEffect(() => {
    state.setDetailBox(false);
    state.setHierarchyDisable(true);
    state.setFolderParentUnitIds([]);

    ApiFolders()
      .getFolderHierarchy(state.folderUnitId, state.folderId)
      .then((res) => {
        state.setFolderParentUnitIds(res.data.parentUnitIds);
        state.setFoldersBreadCrumbs(res.data.hierarchy);
        state.setResultFromSearch(res.data.resultFromSearch);
        state.setSelectedDocumentsForDelete([]);
        state.setSelectedByDragging(false);
        state.setSelectedRowIndex(null);
        state.setHoveredRowIndex(null);
        state.setDetailBox(false);

        if (res.data.length > 0) {
          state.setFolderId(
            res.data.hierarchy[res.data.hierarchy.length - 1]?.folderId
          );
        }
      })
      .catch((err) => {
        console.log(err);
        state.setLoading(false);
        state.setHierarchyDisable(false);
        swal({
          title: err,
          icon: "error",
          buttons: "OK",
        });
      });
  }, [state.folderId]);

  /**
   * Load documents for current folder
   */
  useEffect(() => {
    if (state.skipFolderIdUpdate1.current) {
      state.setHierarchyDisable(false);
      state.skipFolderIdUpdate1.current = false;
      return;
    }

    state.setHierarchyDisable(true);
    state.setLoading(true);

    ApiDocuments()
      .getDocuments({
        folderUnitId: state.folderUnitId,
        folderId: state.folderId,
        searchString: state.searchTerm,
        tags: state.selectedTags,
      })
      .then((res) => {
        handlers.handleData(res);
        state.setHoveredRowIndex(null);
        state.setSelectedRowIndex(null);
        state.setHierarchyDisable(false);
        state.setUploadInProgress(false);
        state.setResultFromSearch(res.data.resultFromSearch);
        state.setSelectedDocumentsForDelete([]);
        state.setSelectedByDragging(false);
        state.setDetailBox(false);

        if (state.folderId !== 0) state.setLoading(false);
        if (state.foldersBreadCrumbs.length <= 0) {
          state.skipFolderIdUpdate1.current = true;
          state.setFolderId(res.data.folderId);
          state.setLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        state.setLoading(false);
        state.setHierarchyDisable(false);
        swal({
          title: err,
          icon: "error",
          buttons: "OK",
        });
      });
  }, [state.folderId]);

  /**
   * Handle dropped folder upload
   */
  useEffect(() => {
    if (state.droppedFolder?.length > 0) {
      state.setUploadFolderModal(true);
    }
  }, [state.droppedFolder]);

  useEffect(() => {
    if (state.droppedFile?.length > 0) {
      state.setUploadFileModal(true);
    }
  }, [state.droppedFile]);

  /**
   * Handle search and filter changes
   */
  useEffect(() => {
    if (
      state.searchTerm === "" &&
      state.selectedTags.length === 0 &&
      state.resultFromSearch &&
      state.clearSearch
    ) {
      searchHandlers.handleSearch();
    }
    state.setClearSearch(false);
  }, [state.searchTerm, state.selectedTags, state.clearSearch]);

  /**
   * Handle page navigation warnings
   */
  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (state.deletionInProgress) {
        return "Files deletion in progress. Are you sure you want to leave this page?\nفائلز حذف کی جا رہی ہیں ۔ کیا آپ اس پیج کو ریلوڈ کرنا چاھتے ہیں؟";
      } else if (state.uploadInProgress) {
        return "Files uploading in progress. Are you sure you want to leave this page?\nفائلز اپلوڈ کی جا رہی ہیں ۔ کیا آپ اس پیج کو ریلوڈ کرنا چاھتے ہیں؟";
      }
    });

    return () => unblock();
  }, [state.deletionInProgress, state.uploadInProgress, history]);

  // ==================== Event Handlers ====================

  /**
   * Toggle upload options visibility
   */
  const toggleUploadOptions = useCallback(() => {
    state.setShowUploadOptions(!state.showUploadOpions);
  }, [state.showUploadOpions]);

  /**
   * Toggle modals
   */
  const toggleFileModal = useCallback(
    () => state.setUploadFileModal(!state.uploadFileModal),
    [state.uploadFileModal]
  );

  const toggleFolderModal = useCallback(
    () => state.setUploadFolderModal(!state.uploadFolderModal),
    [state.uploadFolderModal]
  );

  /**
   * Render action buttons for adding folders and files
   */
  const ButtonsComponent = (className) => {
      return CheckPermission("CanAddDocuments") ? (
        <ButtonGroup className={className}>
          <Button
            disabled={state.deletionInProgress}
            color="white"
            onClick={() => state.setNewFolderModal(true)}
          >
            <FaFolderPlus size={15} color="#FFCA28" />
            &nbsp; <GetLanguageString props={"document_new_folder"} />
          </Button>
          &nbsp;&nbsp;
          <Button
            disabled={state.deletionInProgress}
            color="white"
            onClick={() => {
              toggleUploadOptions();
            }}
          >
            {" "}
            <FiUpload size={15} color="#35A29F" />
            &nbsp;
            <GetLanguageString props={"document_upload_file"} />
          </Button>{" "}
        </ButtonGroup>
      ) : null;
    };

  /**
   * Tooltip toggle handler
   */
  const toggleTooltip = useCallback(
    (action, index) => {
      state.setTooltipOpen((prev) => ({
        ...prev,
        [`${action}-${index}`]: !prev[`${action}-${index}`],
      }));
    },
    []
  );

  const toggleTab = useCallback(
    (tab) => {
      if (state.activeTab !== tab) {
        state.setActiveTab(tab);
      }
    },
    [state.activeTab]
  );

  const openFolder = useCallback((file) => {
    state.setFolderId(file.id);
    state.setSearchTerm("");
    state.setSelectedTags([]);
    state.setFolderUnitId(file.folderUnitId ? file.folderUnitId : state.folderUnitId);
    if (file.folderUnitId) {
      state.setUnitFolderUnitId(file.folderUnitId);
    }
  }, []);

  const tabs = [
    {
      id: "unitFolders",
      label: "unit_unit_table_name",
    },
    {
      id: "otherFiles",
      label:
        state.foldersBreadCrumbs?.length > 0
          ? state.foldersBreadCrumbs[state.foldersBreadCrumbs?.length - 1]?.folderName
          : state.loggedInUnitName.split("/")[0].trim(),
    },
  ];

  // ==================== Render ====================

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <FormPrompt hasUnsavedChanges={state.deletionInProgress} />
      <FormPrompt hasUnsavedChanges={state.uploadInProgress} />

      <ToastContainer
        autoClose={3000}
        bodyClassName={GetSelectedLanguage()}
        closeOnClick
        pauseOnHover={false}
        draggable
      />

      {/* Modals */}
      {state.newFolderModal && (
        <AddNewFolder
          setNewFolderModal={state.setNewFolderModal}
          parentFolderId={state.folderId}
          uploadRefreshDocuments={handlers.uploadRefreshDocuments}
          documents={state.documents}
          setDocuments={state.setDocuments}
        />
      )}

      {state.editFolderModal && (
        <EditFolder
          setEditFolderModal={state.setEditFolderModal}
          parentFolderId={state.folderId}
          uploadRefreshDocuments={handlers.uploadRefreshDocuments}
          folderId={state.editValue}
        />
      )}

      {state.editFileModal && (
        <EditFile
          setEditFileModal={state.setEditFileModal}
          parentFolderId={state.folderId}
          uploadRefreshDocuments={handlers.uploadRefreshDocuments}
          fileId={state.editValue}
        />
      )}

      {state.uploadFolderModal && (
        <UploadFolder
          uploadFolderModal={state.uploadFolderModal}
          setUploadFolderModal={state.setUploadFolderModal}
          parentFolderId={state.folderId}
          uploadRefreshDocuments={handlers.uploadRefreshDocuments}
          setUploadInProgress={state.setUploadInProgress}
          uploadInProgress={state.uploadInProgress}
          droppedFolder={state.droppedFolder}
          setDroppedFolder={state.setDroppedFolder}
          documents={state.documents}
          setDocuments={state.setDocuments}
        />
      )}

      {state.uploadFileModal && (
        <UploadFile
          setUploadFileModal={state.setUploadFileModal}
          folderId={state.folderId}
          uploadRefreshDocuments={handlers.uploadRefreshDocuments}
          documents={state.documents}
          setDocuments={state.setDocuments}
          droppedFile={state.droppedFile}
          setDroppedFile={state.setDroppedFile}
          setUploadInProgress={state.setUploadInProgress}
        />
      )}

      {/* Upload Options Overlay */}
      <UploadOptionsOverlay
        isVisible={state.showUploadOpions}
        overlayRef={state.overlayRef}
        onFileUpload={() => {
          toggleUploadOptions();
          toggleFileModal();
        }}
        onFolderUpload={() => {
          toggleUploadOptions();
          toggleFolderModal();
        }}
      />

      {/* Main Content */}
      <div className="ibox">
        {/* Header with search and filters */}
        <DocumentHeader
          searchTerm={state.searchTerm}
          handleSearchChange={searchHandlers.handleSearchChange}
          selectedTags={state.selectedTags}
          handleSearchTagsChange={searchHandlers.handleSearchTagsChange}
          documentTagsList={state.documentTagsList}
          handleKeyDown={searchHandlers.handleKeyDown}
          handleSearch={searchHandlers.handleSearch}
          handleClearSearch={searchHandlers.handleClearSearch}
          deletionInProgress={state.deletionInProgress}
          uploadInProgress={state.uploadInProgress}
          searchBoxRef={state.searchBoxRef}
          customOption={customOption}
          customMultiValue={customMultiValue}
        />

        <div className="ibox-content">
          {/* Breadcrumb Navigation */}
          <BreadcrumbNavigation
            foldersBreadCrumbs={state.foldersBreadCrumbs}
            onBreadcrumbClick={handlers.handleBreadCrumbs}
            hierarchyDisable={state.hierarchyDisable}
            deletionInProgress={state.deletionInProgress}
          >
            {state.documents.length !== 0 &&
              !state.folderParentUnitIds.includes(state.unitFolderUnitId) &&
              !state.resultFromSearch &&
              state.activeTab !== "unitFolders" && (
                <div>{ButtonsComponent(GetCustomStyles().unit_btn_style)}</div>
              )}
          </BreadcrumbNavigation>

          {/* Error Alert */}
          {state.error && (
            <Alert color="danger" style={{ marginTop: "20px" }}>
              {state.error}
            </Alert>
          )}

          <div className="tabs-container">
            <Nav tabs>
               {tabs.map((tab) => {
                    // Show the unitFolders tab only when unitFolders.length > 0
                    if (tab.id === "unitFolders" && state.unitFolders.length > 0) {
                    return (
                        <NavItem key={tab.id}>
                        <NavLink
                            className={state.activeTab === tab.id ? "active" : ""}
                            onClick={() => toggleTab(tab.id)}
                        >
                            <GetLanguageString props={tab.label} />
                        </NavLink>
                        </NavItem>
                    );
                    }

                    // Show all other tabs normally
                    if (tab.id !== "unitFolders") {
                    return (
                        <NavItem key={tab.id}>
                        <NavLink
                            className={
                            state.activeTab !== "otherFiles"
                                ? tab.id === "otherFiles" &&
                                state.unitFolders.length === 0
                                ? `active ${isUrdu(tab.label) ? "ur" : ""}`
                                : isUrdu(tab.label)
                                ? "ur"
                                : ""
                                : state.activeTab === tab.id
                                ? `active ${isUrdu(tab.label) ? "ur" : ""}`
                                : isUrdu(tab.label)
                                ? "ur"
                                : ""
                            }
                            onClick={() => toggleTab(tab.id)}
                        >
                            {state.foldersBreadCrumbs?.length > 0
                            ? state.foldersBreadCrumbs[
                                state.foldersBreadCrumbs?.length - 1
                                ]?.folderName
                            : state.loggedInUnitName.split("/")[0].trim()}
                        </NavLink>
                        </NavItem>
                    );
                    }

                    // Return null in case no condition is met (for unitFolders when length is 0)
                    return null;
                })}
            </Nav>
            <TabContent activeTab={state.activeTab}>
              <TabPane tabId="otherFiles">
                <DocumentTable
                  state={state}
                  t={t}
                  handlers={handlers}
                  openFolder={openFolder}
                  toggleTooltip={toggleTooltip}
                  formatSize={formatSize}
                  getFileIcon={getFileIcon}
                  getUnitName={getUnitName}
                  dropzoneHandlers={dropzoneHandlers}
                  renderButtons={() => ButtonsComponent("")}
                />
              </TabPane>
              <TabPane tabId="unitFolders">
                <DocumentUnitFoldersTable
                  state={state}
                  t={t}
                  handlers={handlers}
                  openFolder={openFolder}
                  formatSize={formatSize}
                  getFileIcon={getFileIcon}
                  getUnitName={getUnitName}
                />
              </TabPane>
            </TabContent>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(Documents);
